import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setRgAddress } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import { selectGeocodeService } from '@rsos/reduxDataStore/src/features/gisServices/gisServicesSlice';
import { type SinatraState } from '@rsos/sinatra';
import getExtraInfoOrMeta from '@rsos/utils/getExtraInfoOrMeta';
import {
  reverseGeocodeLocationToAddress,
  ReverseGeocodeResult,
} from './helpers';

interface RootState {
  sinatra: SinatraState;
}

/** Subset of a 911 event's location data that this hook reads. */
export interface ReverseGeocodeLocationData {
  caller_id?: string;
  latitude?: string | number;
  longitude?: string | number;
  location_time?: number;
  address_single_line?: string;
  rgAddress?: string;
}

export interface UseReverseGeocodeParams {
  /** Is it a 911 event secondary (ANI/ALI) location? */
  isSecondaryLocation?: boolean;
  /** 911 event's location data. May be null/undefined when the event has no location yet. */
  location?: ReverseGeocodeLocationData | null;
  /** 911 event's location index. */
  locationIndex?: number;
  /** Which reverse geocoding service to use. */
  service?: string;
}

type FoundAddress = ReverseGeocodeResult & { callerID?: string };

/**
 * Reverse geocode hook that determines which reverse geocode service to use then stores the reverse
 * geocoded address and addressObject in Redux. If it is a premium account, the location
 * will be reverse geocoded through the GIS geocode service url if available, otherwise use the
 * default Esri geocode service url. If it is a non-premium account, use the Google reverse
 * geocode service.
 */
const useReverseGeocode = ({
  isSecondaryLocation,
  location,
  locationIndex,
  service,
}: UseReverseGeocodeParams) => {
  const dispatch = useDispatch();

  const irpVersion = useSelector(
    (state: RootState) => state.sinatra.irp.irpVersion,
  );

  const gisGeocodeService = useSelector(selectGeocodeService);

  const gisGeocodeServiceUrl = gisGeocodeService?.gis_service_url || null;

  const gisGeocodeServiceToken = gisGeocodeService?.token || null;

  // The event may have no location yet (e.g. a call before ALI arrives), in
  // which case callers can pass null/undefined. Guard against that so the
  // field reads below never dereference a nullish location.
  const loc = location ?? {};

  const callerID = loc.caller_id;

  const latitude = parseFloat(String(loc.latitude ?? ''));

  const longitude = parseFloat(String(loc.longitude ?? ''));

  const locationTime = loc.location_time;

  const hasLocationChanged = !!(
    callerID &&
    latitude &&
    longitude &&
    locationTime
  );

  const hasSinglelineAddress = !!(
    loc.address_single_line && loc.address_single_line.trim() !== ''
  );

  const hasReverseGeocodeAddress = !!loc.rgAddress;

  const { payloadID, requestID } = getExtraInfoOrMeta(loc) || {};

  const [isReverseGeocoding, setIsReverseGeocoding] = useState(false);

  const [foundAddress, setFoundAddress] = useState<FoundAddress | null>(null);

  // Reverse geocode a location's coordinates to a formatted address & addressObject.
  useEffect(() => {
    if (
      latitude &&
      longitude &&
      hasLocationChanged &&
      !hasReverseGeocodeAddress &&
      !hasSinglelineAddress &&
      service
    ) {
      reverseGeocodeLocationToAddress({
        latitude,
        longitude,
        onReverseGeocoding: setIsReverseGeocoding,
        service,
        serviceUrl: gisGeocodeServiceUrl,
        token: gisGeocodeServiceToken,
      })
        .then(response =>
          response
            ? setFoundAddress({ ...response, callerID })
            : setFoundAddress(null),
        )
        .catch(() => {
          setFoundAddress(null);
        });
    }
  }, [
    callerID,
    gisGeocodeServiceUrl,
    gisGeocodeServiceToken,
    hasLocationChanged,
    hasReverseGeocodeAddress,
    hasSinglelineAddress,
    latitude,
    longitude,
    service,
  ]);

  // If reverse geocode returns an address & address object, dispatch it to emergencyCalls reducer.
  useEffect(() => {
    if (foundAddress) {
      const {
        address,
        addressObject,
        rgAddressProvider,
        callerID: foundCallerID,
      } = foundAddress;

      // Only dispatch if the callerID from when the request was made matches
      // the current callerID. This prevents race conditions when the user
      // switches calls before the geocode request completes.
      if (foundCallerID !== callerID) {
        setFoundAddress(null);
        return;
      }

      dispatch(
        setRgAddress({
          address,
          addressObject,
          callerID,
          isSecondaryLocation,
          locationIndex,
          rgAddressProvider,
        }),
      );

      setFoundAddress(null);
    }
  }, [
    callerID,
    dispatch,
    foundAddress,
    locationIndex,
    isSecondaryLocation,
    irpVersion,
    payloadID,
    requestID,
  ]);

  return { isReverseGeocoding };
};

export default useReverseGeocode;
